import { Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { filesApi } from '../filesApi';
import { generatableCategory, sampleUploadFile } from '../utils/sampleUpload';
import type { FileCategory } from '../fileTypes';

/**
 * The upload form.
 *
 * Every constraint shown here — which categories exist, what each accepts, how large a file
 * may be — comes from the server's registry, never from a hard-coded list. The client
 * cannot offer something the server would then refuse, and adding a category on the server
 * needs no change here.
 *
 * The checks are a courtesy, not a control: the server validates the extension, the content
 * type, the magic bytes, and the size again, because anything decided in a browser is
 * decided by whoever is holding the browser.
 */
export function UploadFileModal({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [categories, setCategories] = useState<FileCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [category, setCategory] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Derived from what the server sent, so a renamed or removed category takes the button
  // with it rather than leaving one that generates a file the upload would refuse.
  const generatable = generatableCategory(categories);

  /*
    Fetch only. The parent mounts this modal when it opens and unmounts it when it closes,
    so every open starts from fresh state — no reset pass to write, and none to forget.
  */
  useEffect(() => {
    const controller = new AbortController();

    filesApi
      .categories(controller.signal)
      .then((loaded) => {
        setCategories(loaded);

        // One target is not a choice — preselect it rather than asking.
        if (loaded.length === 1) {
          setCategory(loaded[0].key);
        }
      })
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError('The upload categories could not be loaded.');
      })
      .finally(() => setLoadingCategories(false));

    return () => controller.abort();
  }, []);

  const selected = categories.find((c) => c.key === category) ?? null;

  function pick(chosen: File | null) {
    setError(null);

    if (!chosen || !selected) {
      setFile(chosen);
      return;
    }

    const extension = chosen.name.slice(chosen.name.lastIndexOf('.')).toLowerCase();

    if (!selected.allowedExtensions.includes(extension)) {
      setError(`${selected.name} accepts ${selected.allowedExtensions.join(', ')}.`);
      setFile(null);
      return;
    }

    if (chosen.size > selected.maxSizeBytes) {
      setError(`That file is ${formatBytes(chosen.size)}. The limit is ${formatBytes(selected.maxSizeBytes)}.`);
      setFile(null);
      return;
    }

    setFile(chosen);
  }

  async function submit() {
    if (!category || !file) return;

    setUploading(true);
    setError(null);

    try {
      await filesApi.upload(category, file);
      onUploaded();
      onClose();
    } catch (caught) {
      // The server's message is the useful one: it names the actual rule that was broken.
      setError(
        caught instanceof ApiError ? caught.message : 'The file could not be uploaded.',
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload file"
      size="md"
      id="upload-file-modal"
      /*
        Offered only when a category the server accepts can actually be produced — CSV is the
        one text format, and every other category takes a binary the server verifies by magic
        bytes. With no such category there is no button, rather than one that generates a file
        the upload would then refuse.
      */
      autogenerate={
        generatable
          ? {
              onGenerate: () => {
                setCategory(generatable.key);
                setFile(sampleUploadFile());
                setError(null);
              },
              disabled: uploading || loadingCategories,
            }
          : undefined
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            id="upload-file-submit"
            onClick={() => void submit()}
            loading={uploading}
            loadingLabel="Submitting"
            disabled={!category || !file}
            leading={<Upload className="h-4 w-4" aria-hidden="true" />}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          id="upload-file-category"
          name="upload-file-category"
          label="Category"
          description="Decides where the file is filed, what it may contain, and how large it may be."
          value={category}
          onChange={(next) => {
            setCategory(next);

            // A file chosen for the previous category may not be valid for this one.
            setFile(null);
            setError(null);
          }}
          loading={loadingCategories}
          options={categories.map((c) => ({ value: c.key, label: c.name }))}
          placeholder="Choose a category"
        />

        <div>
          <label
            htmlFor="upload-file-input"
            className="mb-1.5 block text-sm font-medium text-slate-700"
          >
            File
          </label>

          <input
            ref={inputRef}
            id="upload-file-input"
            name="upload-file-input"
            type="file"
            disabled={!selected}
            accept={selected?.allowedExtensions.join(',')}
            onChange={(event) => pick(event.target.files?.[0] ?? null)}
            className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
          />

          <p className="mt-1.5 text-xs text-slate-500">
            {selected
              ? `${selected.allowedExtensions.join(', ')} · up to ${formatBytes(selected.maxSizeBytes)}`
              : 'Choose a category first.'}
          </p>
        </div>

        {file ? (
          <p className="text-xs text-slate-500">
            <span className="text-slate-700">{file.name}</span> · {formatBytes(file.size)}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
